/**
 * Polls plan-bake jobs for overlays whose tiles are being generated.
 *
 * The orthophoto equivalent (useTileStatusPoller) carries a lot of ortho-only
 * machinery — placeholder-preview adoption, unknown-status self-heal for a
 * 12-step upload flow. A plan overlay needs none of that: its proxy image is
 * always present (the browser rendered it at import), so the only job here is
 * to flip tileStatus when the bake lands and to roll back to the previous
 * generation when it fails. Kept as its own hook so the proven ortho poller
 * stays untouched.
 *
 * Mounted once, next to useTileStatusPoller in EditorPage.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useProjectStore, useUIStore } from '../stores';
import { getTilesetStatus } from '../services/tileApi';
import type { Overlay } from '../stores/projectStore';

const POLL_INTERVAL = 5000; // ms — matches useTileStatusPoller cadence

// A rollback needs the PREVIOUS tileset's status, and that GET can fail
// transiently. Retry on later polls, but not forever: past the cap the overlay
// is flipped to 'error' so the user gets a re-bake affordance instead of a
// spinner that never resolves.
const MAX_ROLLBACK_FAILURES = 5;
const rollbackFailures = new Map<string, number>();

// The status endpoint requires auth, so a share visitor (or an expired session)
// can 403 every cycle. Back OFF hard rather than stopping: giving up entirely
// used to strand a live job — a minute of lost connectivity was enough to leave
// the overlay on 'generating' forever, and because that state hides the Bake
// button there was no way to recover without a reload.
const SLOW_POLL_AFTER_FAILURES = 12;
const SLOW_POLL_EVERY = 12; // cycles ≈ once a minute at a 5 s tick
const statusFailures = new Map<string, number>();
let pollTick = 0;

/** Overlays with a bake job worth asking the server about. */
function isPending(o: Overlay): boolean {
  if (!o.tilesetId) return false;
  return o.tileStatus === 'generating' || o.tileStatus === 'pending';
}

/** After repeated failures keep checking, just far less often. */
function shouldPollNow(o: Overlay): boolean {
  const failures = statusFailures.get(o.tilesetId!) ?? 0;
  if (failures < SLOW_POLL_AFTER_FAILURES) return true;
  return pollTick % SLOW_POLL_EVERY === 0;
}

// A rollback must announce itself ONCE. The entity write can keep losing a
// version race and re-run this branch every tick, which used to repeat the same
// toast every 5 seconds.
const rollbackNotified = new Set<string>();

// ...and the rollback WRITE needs its own ceiling. If that write keeps failing
// (a share viewer's 403 — the poller is mounted unconditionally — or a 5xx),
// the overlay is restored to 'generating' by the store's rollback, so the next
// tick re-enters this branch and retries forever. Silently, now that the toast
// fires once. Cap it and let the plain error flip end the loop.
const MAX_ROLLBACK_WRITES = 3;
const rollbackWrites = new Map<string, number>();

export default function useOverlayBakePoller() {
  const siteId = useProjectStore((s) => s.meta.siteId);
  const overlays = useProjectStore((s) => s.data.overlays);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // The effect keys off the pending SET, not the overlays array, so an
  // unrelated re-render (an opacity drag, say) cannot restart the interval and
  // fire an immediate extra poll. Guarding inside the effect instead cannot
  // work: React runs the previous cleanup first, which has already cleared the
  // timer, so any "is it still running?" check is dead by construction.
  const pendingKey = useMemo(
    () => overlays.filter(isPending).map((o) => o.tilesetId!).sort().join(','),
    [overlays],
  );

  useEffect(() => {
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!siteId || !pendingKey) {
      stop();
      return;
    }

    const poll = async () => {
      const state = useProjectStore.getState();
      const currentSiteId = state.meta.siteId;
      if (!currentSiteId) return;
      const stillPending = state.data.overlays.filter(isPending);
      if (stillPending.length === 0) {
        stop();
        return;
      }

      pollTick += 1;
      for (const overlay of stillPending) {
        const tilesetId = overlay.tilesetId!;
        if (!shouldPollNow(overlay)) continue;
        // Every write below re-reads the LIVE overlay first. The awaits in
        // this loop are windows where the user can commit a crop/erase (bake
        // params) or a re-bake can swap tilesetId; writing the tick-start
        // snapshot back would clobber those edits in store + D1, and a status
        // fetched for a superseded tileset must not be stamped onto the new
        // attempt (or onto a different project after a switch).
        const liveOverlay = () => {
          const cur = useProjectStore.getState().data.overlays
            .find((o) => o.id === overlay.id);
          return cur && cur.tilesetId === tilesetId ? cur : null;
        };
        try {
          const status = await getTilesetStatus(currentSiteId, tilesetId);
          statusFailures.delete(tilesetId); // reachable again
          if (status.status !== 'ready' && status.status !== 'error') continue;

          // A failed bake must not strand the overlay: the previous generation
          // was never deleted (the server only deletes a prior on a VERIFIED
          // success), so restore it and keep serving the older tiles.
          const rollbackAttempts = rollbackWrites.get(tilesetId) ?? 0;
          if (status.status === 'error' && status.priorTilesetId
              && rollbackAttempts < MAX_ROLLBACK_WRITES) {
            try {
              const prior = await getTilesetStatus(currentSiteId, status.priorTilesetId);
              if (prior.status === 'ready') {
                const live = liveOverlay();
                if (!live) continue; // superseded mid-await — nothing to roll back
                rollbackWrites.set(tilesetId, rollbackAttempts + 1);
                useProjectStore.getState().updateOverlay(overlay.id, {
                  tilesetId: prior.id,
                  tileStatus: 'ready',
                  tileMinZoom: prior.minZoom,
                  tileMaxZoom: prior.maxZoom,
                  // Restore the fingerprint the RESTORED tiles were actually
                  // baked at, so they keep serving at full resolution in the
                  // right place. Only accept it when it describes THIS tileset:
                  // the id comes from the server's lineage while the
                  // fingerprint is the client's belief, and if those have
                  // drifted apart (a second tab, or a bake-start write that
                  // lost a race) pairing them would render one generation's
                  // tiles under another generation's placement — precisely the
                  // confidently-wrong output this whole mechanism prevents.
                  // When they disagree, fall back to the proxy.
                  bake: live.bake
                    ? (() => {
                        const prev = live.bake!.previousBaked;
                        const trustworthy = !!prev && prev.tilesetId === prior.id;
                        return {
                          ...live.bake!,
                          lastBaked: trustworthy ? prev : undefined,
                          previousBaked: undefined,
                          needsRebake: trustworthy ? undefined : true,
                        };
                      })()
                    : undefined,
                });
                rollbackFailures.delete(tilesetId);
                // A console warning is invisible to the user, who is watching a
                // "Baking…" chip that would simply disappear. Say what happened
                // — but only once: this branch re-runs every tick while the
                // entity write keeps losing a version race.
                if (!rollbackNotified.has(tilesetId)) {
                  rollbackNotified.add(tilesetId);
                  // Branch on whether the fingerprint was actually TRUSTED, not
                  // merely present: a mismatched one is discarded above, so
                  // reporting "kept the previous full-resolution version" from
                  // its presence alone would promise sharpness while the map
                  // shows the proxy.
                  const keptFullRes =
                    !!live.bake?.previousBaked
                    && live.bake.previousBaked.tilesetId === prior.id;
                  useUIStore.getState().addNotification({
                    type: 'warning',
                    title: 'Bake failed',
                    message: keptFullRes
                      ? `${overlay.name} — kept the previous full-resolution version. Press Re-bake to try again.`
                      : `${overlay.name} — showing the preview image. Press Re-bake to try again.`,
                  });
                }
                console.warn(
                  `[BakePoller] ${overlay.name}: bake failed (${status.error ?? 'unknown'}); ` +
                  `kept previous full-resolution tiles ${prior.id}`,
                );
                continue;
              }
              // Prior exists but isn't serving — fall through to the error flip.
            } catch {
              const n = (rollbackFailures.get(tilesetId) ?? 0) + 1;
              rollbackFailures.set(tilesetId, n);
              if (n < MAX_ROLLBACK_FAILURES) continue;
            }
          }

          if (!liveOverlay()) continue; // superseded mid-await — stale status
          useProjectStore.getState().updateOverlay(overlay.id, {
            tileStatus: status.status as 'ready' | 'error',
            tileMinZoom: status.minZoom,
            tileMaxZoom: status.maxZoom,
          });
          console.log(`[BakePoller] ${overlay.name}: bake → ${status.status}`);
        } catch (err) {
          const msg = (err as Error).message || '';
          statusFailures.set(tilesetId, (statusFailures.get(tilesetId) ?? 0) + 1);
          console.warn('[BakePoller] Poll failed:', msg);
          // The tileset row is gone (deleted, or never created) — there is
          // nothing left to wait for, so surface it as a failed bake rather
          // than polling a dead id forever.
          if (msg.includes('404') || msg.includes('not found') || msg.includes('Not Found')) {
            if (liveOverlay()) {
              useProjectStore.getState().updateOverlay(overlay.id, { tileStatus: 'error' });
            }
          }
        }
      }
    };

    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL);

    return stop;
  }, [siteId, pendingKey]);
}
